/**
 * SlideSenseSidebar
 *
 * Uses the shared AppSidebar shell so Slide Sense gets the same
 * collapsible sidebar as every other module.  Contains:
 *   - "All slides" button
 *   - "My Slides" button with collection count badge
 *   - Recursive folder tree (reuses FolderNav logic)
 *
 * Collapse-aware: shows only icons when the sidebar is minimised.
 */

import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  ArrowDownAZ,
  ArrowUpDown,
  BarChart2,
  BookOpen,
  Check,
  Clock,
  Folder,
  HelpCircle,
  Home,
  LayoutGrid,
  Settings,
} from "@/components/icons"
import { AppSidebar } from "@/components/layouts/app-layout/app-sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { cn } from "@/lib/utils"

import { SidebarFolderItem } from "./SidebarFolderItem"
import { useFolderSortMode } from "../hooks/use-folder-sort-mode"
import { useFolders } from "../hooks/use-folders"
import { useSlideSenseNav } from "../hooks/use-slide-sense-nav"
import { buildParentMap, buildTree, getAncestorIds } from "../utils/folder-tree"

// ---------------------------------------------------------------------------
// Main sidebar component
// ---------------------------------------------------------------------------

export function SlideSenseSidebar() {
  const { t } = useTranslation()
  const { state, isMobile } = useSidebar()
  const isCollapsed = state === "collapsed" && !isMobile

  const { data: folders = [] } = useFolders()
  const {
    selectedFolderId,
    showMyPresentations,
    activeTab,
    collectionCount,
    selectHome,
    selectFolder,
    selectMyPresentations,
    selectAllSlides,
    selectAdmin,
    selectUsage,
    selectHelp,
    isAdmin,
  } = useSlideSenseNav()

  const isLibrary = activeTab === "library"

  const [folderSortMode, setFolderSortMode] = useFolderSortMode()
  const tree = useMemo(() => buildTree(folders, folderSortMode), [folders, folderSortMode])
  const parentMap = useMemo(() => buildParentMap(folders), [folders])

  // Expand / collapse
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const onToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Auto-expand ancestors of selected folder
  useEffect(() => {
    if (!selectedFolderId) return
    const ancestors = getAncestorIds(selectedFolderId, parentMap)
    if (ancestors.size === 0) return
    setExpandedIds((prev) => {
      let changed = false
      for (const id of ancestors) {
        if (!prev.has(id)) {
          changed = true
          break
        }
      }
      if (!changed) return prev
      const next = new Set(prev)
      for (const id of ancestors) next.add(id)
      return next
    })
  }, [selectedFolderId, parentMap])

  return (
    <AppSidebar>
      {/* Top actions */}
      <SidebarGroup className="bg-sidebar sticky top-0 z-40 gap-1 p-2 pb-0">
        <SidebarMenu>
          {/* Home — welcome dashboard */}
          <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuButton
                    className="justify-center"
                    isActive={activeTab === "home"}
                    onClick={selectHome}
                  >
                    <Home className="h-4 w-4" />
                  </SidebarMenuButton>
                </TooltipTrigger>
                <TooltipContent side="right">{t("slideSense.landing.navHome")}</TooltipContent>
              </Tooltip>
            ) : (
              <SidebarMenuButton
                className="gap-2"
                isActive={activeTab === "home"}
                onClick={selectHome}
              >
                <Home className="h-4 w-4" />
                <span>{t("slideSense.landing.navHome")}</span>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>

          {/* All Slides */}
          <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuButton
                    className="justify-center"
                    isActive={isLibrary && !selectedFolderId && !showMyPresentations}
                    onClick={selectAllSlides}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </SidebarMenuButton>
                </TooltipTrigger>
                <TooltipContent side="right">{t("slideSense.allSlides")}</TooltipContent>
              </Tooltip>
            ) : (
              <SidebarMenuButton
                className="gap-2"
                isActive={isLibrary && !selectedFolderId && !showMyPresentations}
                onClick={selectAllSlides}
              >
                <LayoutGrid className="h-4 w-4" />
                <span>{t("slideSense.allSlides")}</span>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>

          {/* My Slides */}
          <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuButton
                    className="justify-center"
                    isActive={isLibrary && showMyPresentations}
                    onClick={selectMyPresentations}
                  >
                    <BookOpen className="text-primary h-4 w-4" />
                  </SidebarMenuButton>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t("slideSense.myPresentations")}
                  {collectionCount > 0 ? ` (${collectionCount})` : ""}
                </TooltipContent>
              </Tooltip>
            ) : (
              <SidebarMenuButton
                className="gap-2"
                isActive={isLibrary && showMyPresentations}
                onClick={selectMyPresentations}
              >
                <BookOpen className="text-primary h-4 w-4" />
                <span>{t("slideSense.myPresentations")}</span>
                {collectionCount > 0 && (
                  <span className="text-muted-foreground ml-auto text-[10px]">
                    {collectionCount}
                  </span>
                )}
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>

          {/* Admin */}
          {isAdmin && (
            <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton
                      className="justify-center"
                      isActive={activeTab === "admin"}
                      onClick={selectAdmin}
                    >
                      <Settings className="h-4 w-4" />
                    </SidebarMenuButton>
                  </TooltipTrigger>
                  <TooltipContent side="right">{t("slideSense.admin")}</TooltipContent>
                </Tooltip>
              ) : (
                <SidebarMenuButton
                  className="gap-2"
                  isActive={activeTab === "admin"}
                  onClick={selectAdmin}
                >
                  <Settings className="h-4 w-4" />
                  <span>{t("slideSense.admin")}</span>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
          )}

          {/* Usage */}
          {isAdmin && (
            <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuButton
                      className="justify-center"
                      isActive={activeTab === "usage"}
                      onClick={selectUsage}
                    >
                      <BarChart2 className="h-4 w-4" />
                    </SidebarMenuButton>
                  </TooltipTrigger>
                  <TooltipContent side="right">{t("slideSense.nav.usage")}</TooltipContent>
                </Tooltip>
              ) : (
                <SidebarMenuButton
                  className="gap-2"
                  data-testid="slide-sense-nav-usage"
                  isActive={activeTab === "usage"}
                  onClick={selectUsage}
                >
                  <BarChart2 className="h-4 w-4" />
                  <span>{t("slideSense.nav.usage")}</span>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
          )}

          {/* Help — renders the user guide as an in-app tab. The page
              swaps in the <SlideSenseHelp /> React component (Tailwind +
              design tokens, screenshots imported via Vite). Sits
              alongside Usage/Admin so the user stays inside the app shell. */}
          <SidebarMenuItem className={cn("flex", isCollapsed ? "justify-center" : "")}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuButton
                    className="justify-center"
                    isActive={activeTab === "help"}
                    onClick={selectHelp}
                  >
                    <HelpCircle className="h-4 w-4" />
                  </SidebarMenuButton>
                </TooltipTrigger>
                <TooltipContent side="right">{t("slideSense.nav.help")}</TooltipContent>
              </Tooltip>
            ) : (
              <SidebarMenuButton
                className="gap-2"
                data-testid="slide-sense-nav-help"
                isActive={activeTab === "help"}
                onClick={selectHelp}
              >
                <HelpCircle className="h-4 w-4" />
                <span>{t("slideSense.nav.help")}</span>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      {/* Folder tree */}
      {!isCollapsed && tree.length > 0 && (
        <SidebarGroup className="p-2 pt-0">
          <div className="flex items-center justify-between gap-2 pr-1">
            <SidebarGroupLabel className="text-[10px] tracking-wider uppercase">
              {t("slideSense.folders")}
            </SidebarGroupLabel>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={t("slideSense.sortFolders")}
                  className="text-muted-foreground hover:text-foreground data-[state=open]:text-foreground -m-0.5 flex size-6 items-center justify-center rounded-sm transition-colors"
                  type="button"
                >
                  <ArrowUpDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setFolderSortMode("manual")}>
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      folderSortMode === "manual" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <Folder className="mr-2 h-3.5 w-3.5" />
                  {t("slideSense.folderSort.manual")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setFolderSortMode("alphabetical")}>
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      folderSortMode === "alphabetical" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <ArrowDownAZ className="mr-2 h-3.5 w-3.5" />
                  {t("slideSense.folderSort.alphabetical")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setFolderSortMode("updated")}>
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      folderSortMode === "updated" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <Clock className="mr-2 h-3.5 w-3.5" />
                  {t("slideSense.folderSort.updated")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <SidebarGroupContent>
            <SidebarMenu>
              {tree.map((node) => (
                <SidebarFolderItem
                  key={node.folder.id}
                  expandedIds={expandedIds}
                  isAdmin={isAdmin}
                  isCollapsed={isCollapsed}
                  node={node}
                  parentMap={parentMap}
                  selectedFolderId={selectedFolderId}
                  onSelectFolder={selectFolder}
                  onToggleExpand={onToggleExpand}
                  onFolderDeleted={(id) => {
                    if (selectedFolderId === id) selectAllSlides()
                  }}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </AppSidebar>
  )
}
